import React, { useEffect, useState } from "react";
import { api, IMG } from "@/lib/api";
import { PageHeader } from "@/components/shared/SectionHeader";
import { Award, Star, Trophy, GraduationCap, Sparkles, ShieldCheck } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const TIER = {
  bronze: { bg: "bg-amber-800/20 text-amber-700 dark:text-amber-500", ring: "ring-amber-700/40" },
  silver: { bg: "bg-slate-400/20 text-slate-600 dark:text-slate-300", ring: "ring-slate-400/40" },
  gold: { bg: "bg-accent/20 text-accent", ring: "ring-accent/50" },
  platinum: { bg: "bg-primary/15 text-primary", ring: "ring-primary/40" },
};

export default function Certification() {
  const [badges, setBadges] = useState([]);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    api.get("/badges").then(r => setBadges(r.data));
    api.get("/leaderboard").then(r => setBoard(r.data));
  }, []);

  return (
    <div>
      <PageHeader
        eyebrow="Certification"
        title="Credentials the wolfdog world trusts."
        description="Every course completed adds to your learning path. Earn recognized badges and certificates as you progress."
        image={IMG.closeup}
      />

      <section className="container-x py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-14">
          <div>
            <div className="eyebrow">Your certification track</div>
            <div className="mt-4 rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between">
                <div className="font-display text-3xl">Ownership Path</div>
                <div className="text-sm text-muted-foreground">4 of 6 complete</div>
              </div>
              <Progress value={66} className="mt-4" />
              <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
                {["Future Owner","Ownership 101","Ownership 201","Ownership 301","Continuing Ed","Mentor"].map((s, i) => (
                  <div key={s} className={"rounded-md border p-3 " + (i < 4 ? "bg-primary/5 border-primary/30" : "border-border")}>
                    <div className="text-xs text-muted-foreground">Level {i + 1}</div>
                    <div className="font-semibold mt-1">{s}</div>
                    <div className="text-xs text-muted-foreground mt-1">{i < 4 ? "Completed" : "In progress"}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="eyebrow">Badges earned</div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              {badges.map((b, i) => {
                const t = TIER[b.tier] || TIER.bronze;
                return (
                  <div key={b.id} data-testid={`badge-${b.id}`} className={"rounded-lg border border-border bg-card p-5 text-center hover:-translate-y-1 transition ring-1 " + t.ring}>
                    <div className={"mx-auto h-14 w-14 rounded-full grid place-items-center " + t.bg}>
                      {b.tier === "gold" ? <Trophy className="h-6 w-6" /> : b.tier === "platinum" ? <Sparkles className="h-6 w-6" /> : b.tier === "silver" ? <ShieldCheck className="h-6 w-6" /> : <GraduationCap className="h-6 w-6" />}
                    </div>
                    <div className="font-display text-lg mt-3">{b.title}</div>
                    <div className="eyebrow mt-1 text-[10px]">{b.tier}</div>
                    <div className="text-xs text-muted-foreground mt-3">{b.description}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-28 rounded-lg border border-border bg-card p-6">
            <div className="flex items-center gap-2 eyebrow"><Star className="h-3.5 w-3.5" /> Leaderboard</div>
            <div className="mt-4 divide-y divide-border">
              {board.map(u => (
                <div key={u.rank} className="py-3 flex items-center gap-4">
                  <div className="w-6 font-display text-xl text-muted-foreground">{u.rank}</div>
                  <div className="flex-1">
                    <div className="font-semibold">{u.name}</div>
                    <div className="text-xs text-muted-foreground">{u.badge}</div>
                  </div>
                  <div className="font-display text-lg">{u.points}</div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
